import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Cable, TrendingUp, Gauge } from 'lucide-react';
import { PipeDashboardSummary } from '../components/PipeDashboardSummary';
import { useUserPermissionsAfterLogin } from '../../admin/hooks/useUserPermissionsAfterLogin';
import { VIEW_PIPE_INVENTORY_PERMISSION } from '../../admin/constants/permissions';

type AnalysisTab = 'pipe-dashboard' | 'production' | 'quality';

export const AnalysisPage: React.FC = () => {
  const [analysisTab, setAnalysisTab] = useState<AnalysisTab>('pipe-dashboard');
  const { hasPermission, isLoaded } = useUserPermissionsAfterLogin();

  if (isLoaded && !hasPermission(VIEW_PIPE_INVENTORY_PERMISSION)) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h2 className="text-lg font-semibold text-blueprint-blue">Access denied</h2>
        <p className="mt-2 text-sm text-heritage-grey">
          You do not have permission to view Pipe Inventory. Ask an administrator to enable
          &quot;View Pipe Inventory&quot; in User Permissions Management or your user profile.
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0 w-full">
      <div className="mx-auto min-w-0 max-w-[2000px]">
        <div className="min-h-0 rounded-lg bg-white shadow-sm">
          <Tabs
            className="w-full min-w-0 overflow-hidden"
            value={analysisTab}
            onValueChange={(value) => setAnalysisTab(value as AnalysisTab)}
          >
            <div className="sticky top-0 z-10 rounded-t-lg border-b border-[#E2E5EA] bg-white">
              <div className="flex items-center gap-1 bg-transparent px-2 pb-0 pt-2 md:px-4 md:pt-3">
                <TabsList className="h-auto w-max justify-start gap-4 rounded-none border-0 bg-transparent p-0 md:gap-6 lg:gap-8">
                  <TabsTrigger
                    className="relative inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-none border-b-[3px] border-transparent px-0.5 pb-3 pt-1.5 text-sm font-medium text-heritage-grey hover:text-blueprint-blue focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:border-hero-red data-[state=active]:text-hero-red"
                    value="pipe-dashboard"
                  >
                    <Cable className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                    Pipe
                  </TabsTrigger>
                  {/* <TabsTrigger
                    className="relative inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-none border-b-[3px] border-transparent px-0.5 pb-3 pt-1.5 text-sm font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red"
                    value="production"
                  >
                    <TrendingUp className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                    Production Analytics
                  </TabsTrigger>
                  <TabsTrigger
                    className="relative inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-none border-b-[3px] border-transparent px-0.5 pb-3 pt-1.5 text-sm font-medium text-heritage-grey hover:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:text-hero-red"
                    value="quality"
                  >
                    <Gauge className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                    Quality Metrics
                  </TabsTrigger> */}
                </TabsList>
              </div>
            </div>

            <TabsContent value="pipe-dashboard" className="mt-0 min-w-0">
              <div className="min-w-0 px-2 py-3 sm:px-4 sm:py-4">
                <PipeDashboardSummary />
              </div>
            </TabsContent>

            <TabsContent value="production" className="mt-0 min-w-0">
              <div className="flex flex-col items-center justify-center px-2 py-12 text-center sm:px-4">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-page-bg">
                  <TrendingUp className="h-8 w-8 text-heritage-grey" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-blueprint-blue">Production Analytics</h3>
                <p className="max-w-sm text-heritage-grey">
                  Production trends and analytics will appear here.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="quality" className="mt-0 min-w-0">
              <div className="flex flex-col items-center justify-center px-2 py-12 text-center sm:px-4">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-page-bg">
                  <Gauge className="h-8 w-8 text-heritage-grey" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-blueprint-blue">Quality Metrics</h3>
                <p className="max-w-sm text-heritage-grey">
                  Quality metrics and compliance data will appear here.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
